import { Button } from "../Button/index.js";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconCheckmark, IconLoader, IconWarning } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/Snackbar/index.tsx
const resolvePromiseContent = (value, closeToast, promiseResult) => {
	if (typeof value === "function") return value(promiseResult, closeToast);
	return value ?? "";
};
const getPromiseTitle = (title, status, closeToast, promiseResult) => resolvePromiseContent(title[status], closeToast, promiseResult);
const getPromiseDescription = (description, status, closeToast, promiseResult) => description ? resolvePromiseContent(description[status], closeToast, promiseResult) : "";
/**
* Визуальный компонент snackbar-тоста (используется через SnackbarCenter / setSnack).
*/
const Snackbar = memo(({ title = "", description, hasButton = false, buttonText, buttonAction, promise, promiseSettings, icon, messageType = "info", autoClose = true, duration = 3e3, closeToast, successResolver, errorResolver }) => {
	const timeoutRef = useRef(void 0);
	const [promiseStatus, setPromiseStatus] = useState(promise ? "pending" : void 0);
	const [promiseText, setPromiseText] = useState();
	const scheduleClose = useCallback(() => {
		if (!autoClose) return;
		timeoutRef.current = setTimeout(() => {
			closeToast();
		}, duration);
	}, [
		autoClose,
		closeToast,
		duration
	]);
	useEffect(() => {
		if (!promise || !promiseSettings) return;
		setPromiseStatus("pending");
		promise.then((data) => {
			setPromiseStatus("fulfilled");
			if (successResolver) setPromiseText(successResolver(data));
		}).catch((error) => {
			setPromiseStatus("rejected");
			if (errorResolver) setPromiseText(errorResolver(error));
		}).finally(() => {
			if (autoClose) scheduleClose();
		});
	}, [
		promise,
		promiseSettings,
		autoClose,
		scheduleClose,
		successResolver,
		errorResolver
	]);
	useEffect(() => {
		if (!autoClose) return;
		if (promise && promiseStatus === "pending") return;
		if (timeoutRef.current) return;
		scheduleClose();
	}, [
		autoClose,
		promise,
		promiseStatus,
		scheduleClose
	]);
	const onMouseEnter = useCallback(() => {
		if (promiseStatus !== "pending") clearTimeout(timeoutRef.current);
	}, [promiseStatus]);
	const onMouseLeave = useCallback(() => {
		if (promiseStatus !== "pending") scheduleClose();
	}, [promiseStatus, scheduleClose]);
	const status = promiseStatus ?? "pending";
	const promiseResult = promiseText;
	const hasSuccess = promise && promiseStatus === "fulfilled" || !promise && messageType === "success";
	const hasError = promise && promiseStatus === "rejected" || !promise && messageType === "error";
	const actualIcon = useMemo(() => {
		if (promiseSettings?.customIcons?.[status]) return promiseSettings.customIcons[status];
		if (promise) switch (promiseStatus) {
			case "pending": return /* @__PURE__ */ jsx(IconLoader, { className: "size-6 animate-spin" });
			case "rejected": return /* @__PURE__ */ jsx(IconWarning, { className: "size-6" });
			case "fulfilled": return /* @__PURE__ */ jsx(IconCheckmark, { className: "size-6" });
			default: return /* @__PURE__ */ jsx(IconLoader, { className: "size-6 animate-spin" });
		}
		return icon ?? null;
	}, [
		icon,
		promise,
		promiseSettings?.customIcons,
		promiseStatus,
		status
	]);
	const actualDescription = useMemo(() => {
		if (promise) {
			if (promiseText) return promiseText;
			return getPromiseDescription(promiseSettings?.description, status, closeToast, promiseResult);
		}
		return description ?? "";
	}, [
		closeToast,
		description,
		promise,
		promiseResult,
		promiseSettings?.description,
		promiseText,
		status
	]);
	const actualTitle = promise ? getPromiseTitle(promiseSettings.title, status, closeToast, promiseResult) : title;
	return /* @__PURE__ */ jsxs("figure", {
		className: cn("bg-neutrals-30 relative z-[10000] flex w-[30rem] min-w-0 max-w-[calc(100vw-2rem)] justify-start gap-2 rounded-2xl px-6 py-4 text-white duration-200", hasSuccess && "bg-system-success-50", hasError && "animate-reject bg-system-error-50", "items-center"),
		onMouseEnter,
		onMouseLeave,
		"data-cy": "snackbar",
		children: [
			/* @__PURE__ */ jsx("div", {
				className: "flex shrink-0 items-center self-center",
				children: actualIcon
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "flex min-w-0 flex-1 flex-col border-white",
				children: [/* @__PURE__ */ jsx("h1", {
					className: "mt-1 text-base font-medium leading-normal",
					"data-cy": "snackbar-title",
					children: actualTitle
				}), actualDescription ? /* @__PURE__ */ jsx("p", {
					className: "text-base font-normal leading-normal",
					children: actualDescription
				}) : null]
			}),
			hasButton ? /* @__PURE__ */ jsx("div", {
				className: "ml-auto shrink-0 self-center",
				children: /* @__PURE__ */ jsx(Button, {
					size: "sm",
					onClick: buttonAction,
					children: buttonText
				})
			}) : null
		]
	});
});
Snackbar.displayName = "Snackbar";
//#endregion
export { Snackbar };

//# sourceMappingURL=index.js.map